import {Injectable} from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  ActivationStart,
  NavigationEnd,
  NavigationStart,
  PRIMARY_OUTLET,
  ParamMap,
  Router,
  UrlSegment,
  UrlTree
} from '@angular/router';
import {filter, first, map, take} from 'rxjs/operators';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {Location} from "@angular/common";
import {UserActionCreatorService} from '@ispm/user/services/user-action-creator.service';
import {CoreActionCreatorService} from "@ispm/core/services/core-action-creator.service";
import {AuthenticationService} from "@ispm/user/services/authentication.service";
import {get, has, isEmpty} from "lodash";
import {Subject} from "rxjs/Subject";
import {empty} from "rxjs/observable/empty";
import {INavigationBarItemInterface, INavigationConfigurationInterface,IRecourceMap} from "@ispm/navigation/interfaces";
import {select, Store} from "@ngrx/store";
import {IUrlParams} from "@ispm/core/interfaces/router-handler.interface";
import * as _ from "lodash";
import {InterpolateService} from "@ispm/shared/services/interpolate.service";
import {RouteEvents} from "@ispm/core/constants/route-events.constant";
import {IgenomicsState} from '../interfaces/genomics-state.interface';
import { testViewBasePath, testViewPathologistPath, testViewWithPatientProfile, testViewWithPatientSearch } from "@ispm/test/routes/test-view-state-names";
import {NavigationHelperService} from "@ispm/core/services/navigation-helper.service";

@Injectable({
  providedIn: 'root'
})
export class RouterManagerService {
  public currentState: string = '';
  public stateBeforeReload: boolean = false;
  public backToDashboard: boolean = false;
  public browserRefresh: boolean = false;
  public selectedState: string = '';
  public parameters: any = {};
  public queryParameters: any = {};
  public fragments: any = '';
  public skipHistory = '';
  public $currentState: BehaviorSubject<string> = new BehaviorSubject<string>(null); // state will be the router name
  public currentPageUrl: string = ''; // todo: getting rid of the page url which was used alone in navigation bar
  private history: string[] = [];


  //routerHandlerServiceFields
  public routeEvents: typeof RouteEvents = RouteEvents;
  private routeChangeSubjects: Map<string, Subject<{ routeEvent: any, resolve: any, reject: any }>> =
    new Map<string, Subject<{ routeEvent: any, resolve: any, reject: any }>>();

  constructor(private router: Router, private activatedRoute: ActivatedRoute, private location: Location, private userActionCreatorService: UserActionCreatorService,
              private coreActionCreatorService: CoreActionCreatorService, private authenticationService: AuthenticationService,
              private interpolateService: InterpolateService, protected store: Store<IgenomicsState>, private navigationHelperService: NavigationHelperService) {
    this.router.events
      .pipe(filter((event) => (event instanceof NavigationEnd || event instanceof ActivationStart || event instanceof NavigationStart)))
      .subscribe((event: ActivationStart) => {
        if (event instanceof ActivationStart) {
          const snapshotParams = this.collectParams(event.snapshot)
          this.parameters = {...this.parameters, ...snapshotParams};
          this.fragments = event.snapshot.fragment;
          if (!!this.queryParameters && !!event.snapshot.queryParams) {
            this.queryParameters = {...this.queryParameters, ...event.snapshot.queryParams};
          } else {
            this.queryParameters = {...event.snapshot.queryParams};
          }
          this.skipHistory = event.snapshot.data && event.snapshot.data.skipHistory // Passed from router data property for the one which needed history skip
        }
        if (event instanceof NavigationStart) {
          if (!this.router?.getCurrentNavigation()?.extras?.relativeTo) {
            this.parameters = {};
            this.queryParameters = {};
          }
          if (has(this.router?.getCurrentNavigation()?.extras?.state, 'clearParams')) { //use this params if you want to clear params for relativeTo page as well
            this.parameters = {};
            this.queryParameters = {};
          }
          this.browserRefresh = !this.router.navigated;
          this.backToDashboard = event.navigationTrigger === 'popstate';
          this.navigationHelperService.setNavigationTriggerState(this.backToDashboard);

          this.selectedState = event.url.split(/[?#]/)[0].replace(/^\//g, '');
          this.loadOnBeforeNavigation(event, this.selectedState);
          this.currentPageUrl = event?.url;
        }
        if (event instanceof NavigationEnd) {
          this.currentState = this.getStateName(event?.url);
          if ((this.router?.getCurrentNavigation()?.extras?.skipLocationChange
              && !(this.currentState === this.selectedState))
            || !this.skipHistory) {
            this.history.push(event.urlAfterRedirects);
          }
          this.$currentState.next(this.currentState);
          this.navigationHelperService.resetNavigationParams();
          this.sendDataToNewRelic(event); // Sending data to new relic
        }
      });
  }

  // fetching the url segments using router url
  private getStateName(url: string): string {
    if (url == '/') {
      return '/';
    }
    try {
      // 1. Parse the URL using router.parseUrl
      const urlTree: UrlTree = this.router?.parseUrl(url);

      // 2. Check for valid URL structure
      if (!urlTree.root || !urlTree.root.children || !urlTree.root.children[PRIMARY_OUTLET]) {
        console.info('Invalid URL structure. Returning empty string.');
        return '/';
      }

      // 3. Extract and join URL segments safely
      const urlSegments: UrlSegment[] = urlTree.root.children[PRIMARY_OUTLET]?.segments;
      return urlSegments?.map((segment) => segment.path).join('/');
    } catch (error) {
      // 4. Handle unexpected errors gracefully
      console.info('Error parsing URL:', error);
      return '/'; // Or provide a default value based on your use case
    }
  }

  /** Method to get params of the route from its parent and grand parents used in MDT back scenario from events*/
  collectParams(routeSnapshot: ActivatedRouteSnapshot): any {
    let params = {};
    let currentSnapshot = routeSnapshot;
    while (currentSnapshot) {
      params = {...params, ...currentSnapshot.params};
      currentSnapshot = currentSnapshot.parent;
    }
    return params;
  }

  // Function to get a specific router parameter by its name
  getRouterParam(paramName: string): Observable<string | null> {
    return this.activatedRoute.paramMap.pipe(
      map((params: ParamMap) => params.get(paramName))
    );
  }

  // Function to get all router parameters as an object
  getAllRouterParams(): Observable<any> {
    return this.activatedRoute.paramMap.pipe(
      map((params: ParamMap) => {
        const paramMapObject: any = {};
        params.keys.forEach(key => {
          paramMapObject[key] = params.get(key);
        });
        return paramMapObject;
      })
    );
  }

  reload(url?: string, state?: any): Promise<any> {
    const currentUrl = url ? url : this.router.url.split(/[?#]/)[0]; // Either question mark / fragments
    const queryParams = this.queryParameters;
    const fragments = this.fragments;
    // This property help us to retain the previous tab details after reload
    this.stateBeforeReload = true;
    return this.router.navigateByUrl('/', {skipLocationChange: true}).then(() => {
      this.router.navigate([currentUrl], {queryParams: queryParams, state: state, fragment: fragments});
      this.stateBeforeReload = false;
    });
  }

  // Reload is causing problem if we are doing reload in the worklist page, blank appears. Reload will take you to different tab inside the dashboard.
  reloadSamePage(url?: string): Promise<any> {
    let currentUrl = url ? url : this.router.url.split(/[?#]/)[0];
    let queryParams = this.queryParameters;

    const prev = this.router.routeReuseStrategy.shouldReuseRoute;
    const prevOSN = this.router.onSameUrlNavigation;
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = 'reload';

    return this.router.navigate([decodeURIComponent(currentUrl)], {
      queryParams: queryParams,
      relativeTo: this.activatedRoute,
      skipLocationChange: true,
      queryParamsHandling: 'preserve'
    }).then(() => {
      this.router.routeReuseStrategy.shouldReuseRoute = prev;
      this.router.onSameUrlNavigation = prevOSN;
    });
  }

  back() {
    if (this.history.length > 0) {
      this.location.back();
    } else {
      this.router.navigateByUrl("/");
    }
  }

  async loadOnBeforeNavigation(navigationObject, state) {
    switch (state) {
      case 'iframetest': {
        let testInd = sessionStorage.getItem("nonClinicalUsage") ? JSON.parse(sessionStorage.getItem("nonClinicalUsage")) : undefined;
        if (testInd && testInd.forTestingOnly === true) {
          return true;
        }
      }
      case 'admin/settings': {
        const result = await this.checkForAuth(navigationObject).toPromise();
            return result;
      }
      case 'document': {
        const userToSet = this.authenticationService.getUser();
        this.authenticationService.setCurrentUser(userToSet);
        return true;
      }
      case 'login':
      case 'saml/login':
      case 'dashboard':
      case 'oauth2/callback':
      case 'smart/launch':
      case 'unauthorized':
      case 'validateStateFromIAM':
      case 'generic-error':
      case 'externalaction':
        const currentNavigation = this.router.getCurrentNavigation();
        if (currentNavigation && currentNavigation.extractedUrl && get(currentNavigation.extractedUrl?.queryParams, 'clearCacheUser')) {
          localStorage.removeItem('user');
          this.authenticationService.setCurrentUser(null); // In case of clearCache user is true deleting user 
        }
        return true;
      default:
        await this.checkForAuth(navigationObject).toPromise(); // Converting observable to promise for await
        this.userActionCreatorService.setOrganizationConfiguration();
        this.userActionCreatorService.setEnvironmentDetails();
        // if(!CoreActionCreatorService.pageIsReloaded()){
        // if user refreshes the browser need to start to calculate user interaction after the refresh has finished
        this.coreActionCreatorService.skipUserInteractionUpdate(false);
        // }
        return true;
        break;
    }
  }

  checkForAuth(navigationObject): Observable<boolean> {
    const queryParams = this.getQueryParams(navigationObject.url);
    if (queryParams && queryParams['u-code']) {
      return this.authenticationService.CheckExternalLogin().pipe(
        map((user) => {
          const userToSet = this.authenticationService.getUser();
          if (userToSet) userToSet.organization = user.organization;
          this.authenticationService.setCurrentUser(userToSet);
          return true; // Indicate successful authentication
        })
      );
    } else {
      return of(true); // Indicate successful authentication without external login
    }
  }
  
  sendDataToNewRelic(navigationObject) {
    const newrelic = window['newrelic'];
    if (newrelic && typeof newrelic == 'object') {
      let user = this.authenticationService.getUser();
      let toState = navigationObject;
      if (user) {
        newrelic.setCustomAttribute("organizationId", user.organization);
        newrelic.setCustomAttribute("organizationName", user.managingOrganization);
        newrelic.setCustomAttribute("userId", user.id);
        newrelic.setCustomAttribute("userName", user.username);
      }
      newrelic.setCustomAttribute("urlGenericName", toState.url);
      newrelic.setCustomAttribute("urlGenericPath", toState.url);
      if (sessionStorage.getItem('appName')) {
        newrelic.setCustomAttribute("ApplicationName", sessionStorage.getItem('appName'));
      } else {
        newrelic.setCustomAttribute("ApplicationName", 'DEFAULT');
      }
      const pageName = `toState.url , application : ${this.queryParameters['appname']}`
      newrelic.setCustomAttribute("pageName", pageName);
       //beacuse of this every console error comes from this file
      /*(function() {
        var originalError = console.error;
        var isCustomError = false;
        console.error = function() {
          if (!isCustomError) {
            var args = Array.prototype.slice.call(arguments);
            if (newrelic && newrelic.addPageAction) {
              newrelic.addPageAction('consoleError', { error: args.join(' ') });
            }
            isCustomError = true;
            originalError.apply(console, args);
            isCustomError = false;
          } else {
            originalError.apply(console, arguments);
          }
        };
      })();*/
    }
  }

  navigate(state, param?, reload = false) {
    this.router.navigate([state], {queryParams: param});
  }

  gotohome() {
    // @ts-ignore
    this.getWebConfiguration().pipe(filter(data => data), take(1)).subscribe(data => {
      // @ts-ignore
      let locatedSelected = false;
      let selectState = 'dashboard';
      let params = {};
      if (!data.navigation) {
        // this.authService.logout({refresh: true});
      } else {
        if (!this.router.navigate(['dashboard.settings.base'])) {
          if (data['currentApp']) {
            if (data['currentApp'].items && data['currentApp'].items[0]) {
              selectState = this.getStateFromUri(data['currentApp'].items[0]);
              params = this.getUrlParams(data['currentApp'].items[0].redirectUrl);
              locatedSelected = true;
            }
          }

          if (!locatedSelected) {
            // @ts-ignore
            const firstEnable = data.navigation.items.find(navItem => !!navItem.enable);
            selectState = this.getStateFromUri(firstEnable);
            params = this.getUrlParams(firstEnable.redirectUrl);
          }
        } else {
          selectState = 'admin/settings';
        }
        this.router.navigate([selectState], {queryParams: params});
      }
    });
  }

  public urlToState(url: string): any {
    const urlArray = url.split(/[?#]/);
    const dict = {};
    if (urlArray.length > 1) {
      urlArray[1].split('&').forEach(prop => {
        const [key, value] = prop.split('=');
        dict[key] = value;
      });
    }
    const params = this.router.parseUrl(url);

    return {
      name: url,
      params: params,
      dict: !isEmpty(dict) ? dict : null
    };
  }

  public getUrlParams(url) {
    const uriState = this.urlToState(url);
    return uriState.params;
  }

  //TODO : check how to migrate
  getRoutingListenerFor(routerEvent: string, statePattern?: string): Observable<any> {
    let pattern = {};
    if (statePattern) {
      pattern = {to: statePattern};
    }
    if (this.routeEvents[routerEvent] && this.routeEvents[routerEvent] in this.router) {
      const event = this.routeEvents[routerEvent];
      if (this.routeChangeSubjects.has(event)) {
        return this.routeChangeSubjects.get(event).asObservable();
      } else {
        const subject = new Subject<any>();
        const handler = this.router[event];
        handler(pattern, (routeEvent) => {
          if (subject.observers.length === 0) {
            // handling cold observable
            return true;
          }
          return new Promise<void>((resolve, reject) => {
            const approve = () => {
              resolve();
              this.navigate(routeEvent.originalTransition());
            };
            subject.next({routeEvent, approve, reject});
          });
        });
        this.routeChangeSubjects.set(event, subject);
        return subject.asObservable();
      }
    } else {
      return empty();
    }
  }

  private getWebConfiguration(): Observable<INavigationConfigurationInterface> {
    return this.store.pipe(select('navigation'));
  }

  public getStateFromUri(navItem: INavigationBarItemInterface) {
    const stateRedirectObject = this.urlToState(navItem.redirectUrl);
    return (stateRedirectObject) ? stateRedirectObject.name || '' : '';
  }

  getUrlFromConfig(data: any, defaultURL: string, urls: Array<IUrlParams>): string {
    // Support conditional url.
    // Getting url objects array and looking for the url that meats the condition.
    // If url array is missing or the condition does not meet, return the default url.
    let urlOut: string = defaultURL;
    if (urls && data) {
      for (const urlConfig of urls) {
        if (urlConfig.fieldPath && urlConfig.fieldValue) {
          if (get(data, urlConfig.fieldPath) == urlConfig.fieldValue) {
            urlOut = urlConfig.URL;
            break;
          }
        }
      }
    }
    return urlOut;
  }

  getUrlObjectFromConfig(data: any, urlsObject: Array<IUrlParams>): IUrlParams {
    // Support conditional url.
    // Getting url objects array and looking for the url object that meats the condition.
    // If url array is missing or the condition does not meet, return the default url object.
    if (!urlsObject) {
      return;
    }
    let urlObjectOut: IUrlParams = urlsObject.find(obj => !obj.hasOwnProperty('fieldPath')); // Default config is the config without 'fieldPath'
    if (data) {
      for (const urlConfig of urlsObject) {
        if (urlConfig.fieldPath && urlConfig.fieldValue) {
          if (get(data, urlConfig.fieldPath) == urlConfig.fieldValue) {
            urlObjectOut = urlConfig;
            break;
          }
        }
      }
    }
    return urlObjectOut;
  }

  getStateToGo(redirectUrl, param = {}, emrIntegrationObj = null, appName = ''): { name, queryParams } {
    param = {...param, ...emrIntegrationObj};
    let url = this.interpolateService.interpolate(redirectUrl, param);
    let queryParams = {};
    if (url) {
      const urlParts = url.split(/[?#]/);
      redirectUrl = urlParts[0];
      const queryParamsString = urlParts[1];
      queryParams = queryParamsString ? queryParamsString.split('&').reduce((acc, param) => {
        const [key, value] = param.split('=');
        acc[key] = value || ''; // handle case when value is undefined
        return acc;
      }, {}) : {};
    }
    const uriState = {name: redirectUrl, queryParams: {appname: '', ...queryParams}};
    if (emrIntegrationObj) {
      uriState.queryParams = Object.assign(uriState.queryParams, emrIntegrationObj);
    }

    const currentApp = sessionStorage.getItem('currentApplication');

    if (currentApp) {
      const app = JSON.parse(currentApp);
      uriState.queryParams.appname = _.get(app, 'alias', '');
    } else if (appName) {
      uriState.queryParams.appname = appName;
    }
   if(_.get(param,"rowData.config.considerRouteParam",false)){
     const additionalPayload:IRecourceMap = {
       firstRevision:_.get(param,"rowData.data.DiagnosticOrder#WorkflowRequest.revisions.0.id"),
       requestedRevision:_.get(param,"rowData.data.WorkflowRequest.revisions_-1_id"),
       activeJob:_.get(param,"rowData.data.DiagnosticOrder#WorkflowRequest.latestRevisionActiveJobId"),
       activeRevision:_.get(param,"rowData.data.DiagnosticOrder#WorkflowRequest.activeRevision"),
       workflowRequest:_.get(param,"rowData.data.DiagnosticOrder#WorkflowRequest.id")
     }
     this.navigationHelperService.setNavigationParams(additionalPayload);
   } else {
     this.navigationHelperService.resetNavigationParams();
   }

    return uriState;
  }

  getQueryParams(url) {
    if (url.indexOf('?') === -1) {
      return;
    }
    const paramArr = url.slice(url.indexOf('?') + 1).split('&');
    const params = {
      appname: undefined
    };
    paramArr.map(param => {
      const [key, val] = param.split('=');
      params[key] = decodeURIComponent(val);
    })
    return params;
  }

  getURL(url) {
    return url.split(/[?#]/)[0];
  }

  setParamsInURL(url, params) {
    const states = url.split('/');
    let finalURL = '';
    for (let state of states) {
      if (state.includes(':')) {
        finalURL += ('/' + params[state.substr(1)]);
      } else {
        finalURL += ('/' + state);
      }
    }
    return finalURL;
  }

  setQueryParamsInURL(url, queryParams) {
    let finalURL = url;
    let first = true;
    for (let key in queryParams) {
      if (first) {
        finalURL += ('?' + key + '=' + queryParams[key]);
        first = false;
      } else {
        finalURL += ('&' + key + '=' + queryParams[key]);
      }
    }
    return finalURL;
  }

  mapStateToUrl(state) {
    switch (state) {
      case "dashboard.pathologist.patient-profile.test":
        return testViewWithPatientProfile;
      case "dashboard.pathologist.profile.test":
        return testViewPathologistPath;
      case "dashboard.patient-dashboard-tests":
        return testViewWithPatientSearch;
      case "dashboard.pathologist.test":
        return testViewBasePath;
      // Add more cases as needed
      default:
        return "login"; // Handle cases with no mapping
    }
  }

}
